'use client';
import { useMemo, useState } from 'react';
import { AiOutlineLock } from 'react-icons/ai';
import { useBenzingaEdge } from '@benzinga/edge';
import { formatLongPrice } from '@benzinga/calendars';
import type { MemberTrade, SecurityTrader } from '@benzinga/gov-trades';

import { getFullStateName, getPoliticalPartyName } from './utils';
import { useRevealOnGrow } from '../useRevealOnGrow';
import { memberHref, securityHref } from './hrefs';
import { SidebarRow } from './SidebarRow';
import { Widget } from './Widget';
import { WidgetIcon, type WidgetIconName } from './WidgetIcon';

type RowKind = 'trade' | 'trader';

interface InsightTab {
  key: string;
  name: string;
  data: (MemberTrade | SecurityTrader)[];
}

interface OverviewInsightCardProps {
  icon: WidgetIconName;
  title: string;
  subtitle: string;
  rowKind: RowKind;
  tabsData: InsightTab[];
}

const STEP = 5;

const TradeInsightRow = ({ rank, trade }: { rank: number; trade: MemberTrade }) => {
  const isSell = (trade.transaction_type ?? '').trim().charAt(0).toUpperCase() === 'S';
  const volume = trade.trade_amount?.split(' - ')[0]?.replace('$', '')?.replaceAll(',', '') ?? '';
  const amount = `${isSell ? '-' : ''}${formatLongPrice(volume).replace('.00', '')}`;
  return (
    <SidebarRow
      rank={rank}
      href={securityHref(trade.ticker)}
      title={trade.ticker}
      subtitle={trade.company_name}
      value={amount}
      valueTone={isSell ? 'neg' : 'pos'}
      pill={{ label: isSell ? 'Sell' : 'Buy', tone: isSell ? 'sell' : 'buy' }}
    />
  );
};

const TraderInsightRow = ({ rank, tone, trader }: { rank: number; tone: 'buy' | 'sell'; trader: SecurityTrader }) => {
  const pct = (trader.ttm_return ?? 0) * 100;
  const label = `${tone === 'buy' && pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
  const subtitle = [getPoliticalPartyName(trader.party ?? ''), trader.chamber, getFullStateName(trader.state ?? '')]
    .map(part => (typeof part === 'string' ? part.trim() : part))
    .filter((part): part is string => Boolean(part) && part !== '-')
    .join(' · ');
  return (
    <SidebarRow
      rank={rank}
      href={memberHref(trader.display_name)}
      title={trader.display_name}
      subtitle={<span className="capitalize">{subtitle}</span>}
      value={label}
      valueChip
      valueTone={tone === 'buy' ? 'pos' : 'neg'}
    />
  );
};

export const OverviewInsightCard = ({ icon, rowKind, subtitle, tabsData, title }: OverviewInsightCardProps) => {
  const initialCount = tabsData.length > 1 ? STEP : STEP + 3;
  const [activeKey, setActiveKey] = useState(tabsData[0]?.key);
  const [limit, setLimit] = useState(initialCount);
  const { hasEdgeSubscription: hasPermission } = useBenzingaEdge();
  const bodyRef = useRevealOnGrow(limit);

  const activeTab = useMemo(() => tabsData.find(tab => tab.key === activeKey) ?? tabsData[0], [activeKey, tabsData]);
  const rows = activeTab?.data ?? [];
  const moreToShow = rows.length > limit;
  const tone: 'buy' | 'sell' = activeTab?.key === 'loser' ? 'sell' : 'buy';

  return (
    <Widget
      icon={<WidgetIcon name={icon} />}
      title={title}
      subtitle={subtitle}
      bodyRef={bodyRef}
      tabs={
        tabsData.length > 1
          ? tabsData.map(tab => (
              <button
                className={`gt-tab ${tab.key === activeTab?.key ? 'active' : ''}`}
                key={tab.key}
                onClick={() => {
                  setActiveKey(tab.key);
                  setLimit(initialCount);
                }}
              >
                {tab.name}
              </button>
            ))
          : undefined
      }
      footer={
        moreToShow ? (
          <button
            className="view-all"
            disabled={!hasPermission}
            onClick={() => hasPermission && setLimit(prev => prev + STEP)}
          >
            {!hasPermission && <AiOutlineLock size={14} />}
            Load More
          </button>
        ) : undefined
      }
    >
      {rowKind === 'trade'
        ? (rows as MemberTrade[])
            .slice(0, limit)
            .map((trade, index) => <TradeInsightRow key={`${trade.ticker}-${index}`} rank={index + 1} trade={trade} />)
        : (rows as SecurityTrader[])
            .slice(0, limit)
            .map((trader, index) => (
              <TraderInsightRow key={`${trader.member_id}-${index}`} rank={index + 1} tone={tone} trader={trader} />
            ))}
    </Widget>
  );
};
